import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconButton } from "@/components/common/IconButton";
import {
	captionStyle,
	cellStyle,
	pageTitleStyle,
	sectionHeadingStyle,
} from "@/components/infra/infra-styles";
import {
	formatInstanceSize,
	getInfraStartFailures,
	type InfraStartFailure,
	type InfraStartFailureCategory,
	type InfraStartFailures,
} from "@/lib/api";
import { ACTION_LINK_CLASS } from "@/lib/link-styles";
import { formatDateTime } from "@/lib/format-datetime";
import {
	START_FAILURE_CATEGORIES,
	START_FAILURE_CATEGORY_COLORS,
	START_FAILURE_CATEGORY_LABELS,
	breakdownByInstance,
	dailyCounts,
	summarizeByCategory,
	type DailyCounts,
	type InstanceBreakdownRow,
} from "@/lib/infra-start-failures";

const PERIOD_DAYS = [7, 30, 90] as const;

const RECENT_LIMIT = 100;

const PLOT_HEIGHT = 140;

const CATEGORY_HINTS: Record<InfraStartFailureCategory, string> = {
	quota: "アカウントの上限。待っても解消しないので上限の引き上げが必要",
	stockout: "AWS 側の在庫切れ。確保待ちに入り、在庫が戻れば起動する",
	otherCapacity: "サブネットの IP 不足など、在庫・上限以外の EC2 起動失敗",
	other: "crash loop・スケジュール不能など、EC2 と無関係の失敗",
};

/**
 * 管理ページの「環境の起動失敗」タブ (/admin/start-failures、SuperAdmin のみ)。起動失敗の台帳を、
 * AWS の在庫切れとクォータ超過を見分けられる形で期間ごとに集計して見せる。
 */
export function StartFailuresPage() {
	const [days, setDays] = useState<number>(30);
	const [data, setData] = useState<InfraStartFailures | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState<InfraStartFailureCategory | null>(null);
	const mounted = useRef(true);

	const load = useCallback(() => {
		setLoading(true);
		getInfraStartFailures(days)
			.then((result) => {
				if (!mounted.current) return;
				setData(result);
				setError(null);
			})
			.catch((err: Error) => {
				if (mounted.current) setError(err.message);
			})
			.finally(() => {
				if (mounted.current) setLoading(false);
			});
	}, [days]);

	useEffect(() => {
		mounted.current = true;
		load();
		return () => {
			mounted.current = false;
		};
	}, [load]);

	const entries = useMemo(() => data?.entries ?? [], [data]);
	const summary = useMemo(() => summarizeByCategory(entries), [entries]);
	const daily = useMemo(
		() => (data === null ? [] : dailyCounts(entries, new Date(data.from), new Date(data.to))),
		[data, entries],
	);
	const breakdown = useMemo(() => breakdownByInstance(entries), [entries]);
	const recent = entries
		.filter((e) => selectedCategory === null || e.category === selectedCategory)
		.slice(0, RECENT_LIMIT);

	return (
		<div
			data-testid="start-failures-page"
			style={{ padding: 32, height: "100%", overflowY: "auto", background: "var(--surface-1)" }}
		>
			<h2 style={pageTitleStyle}>環境の起動失敗</h2>
			<p style={captionStyle({ margin: "6px 0 16px" })}>
				全組織の環境の起動失敗と、GPU の在庫切れで確保待ちに入った記録です。分類は EC2
				のエラーコードで決めます。
				{data?.truncated === true && " 期間内の件数が多いため、古い記録を省いています。"}
			</p>

			<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
				<select
					className="ds-select"
					data-testid="start-failures-period"
					value={days}
					onChange={(e) => setDays(Number(e.target.value))}
					style={{ width: 120, height: 32 }}
				>
					{PERIOD_DAYS.map((d) => (
						<option key={d} value={d}>
							過去 {d} 日
						</option>
					))}
				</select>
				<IconButton
					icon="rotate"
					title="環境の起動失敗を更新"
					data-testid="start-failures-refresh"
					size={28}
					disabled={loading}
					onClick={load}
				/>
			</div>

			{error !== null && (
				<p
					data-testid="start-failures-error"
					style={{ margin: "0 0 16px", fontSize: "var(--text-ui)", color: "var(--danger)" }}
				>
					{error}
				</p>
			)}
			{error === null && data === null && <p style={captionStyle({ margin: 0 })}>読み込み中…</p>}

			{data !== null && (
				<>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
							gap: 12,
							marginBottom: 24,
						}}
					>
						{START_FAILURE_CATEGORIES.map((category) => (
							<CategoryTile
								key={category}
								category={category}
								count={summary[category].count}
								environmentCount={summary[category].environmentCount}
								selected={selectedCategory === category}
								onClick={() =>
									setSelectedCategory((current) => (current === category ? null : category))
								}
							/>
						))}
					</div>

					<section style={{ marginBottom: 32 }}>
						<h3 style={sectionHeadingStyle}>日ごとの件数</h3>
						<DailyChart days={daily} />
					</section>

					<section style={{ marginBottom: 32 }}>
						<h3 style={sectionHeadingStyle}>インスタンスごとの件数</h3>
						{breakdown.length === 0 ? (
							<p style={captionStyle({ margin: 0 })}>期間内の起動失敗はありません。</p>
						) : (
							<InstanceBreakdownTable rows={breakdown} />
						)}
					</section>

					<section>
						<h3 style={sectionHeadingStyle}>
							最近の記録
							{selectedCategory !== null && ` (${START_FAILURE_CATEGORY_LABELS[selectedCategory]})`}
						</h3>
						<p style={captionStyle({ margin: "0 0 8px" })}>
							新しい {RECENT_LIMIT} 件まで表示します。詳細は全体で新しい {RECENT_LIMIT}{" "}
							件にだけ読み込みます。
						</p>
						{recent.length === 0 ? (
							<p style={captionStyle({ margin: 0 })}>該当する記録はありません。</p>
						) : (
							<RecentTable entries={recent} />
						)}
					</section>
				</>
			)}
		</div>
	);
}

/** 分類 1 つの件数。押すと最近の記録をその分類に絞る。 */
function CategoryTile({
	category,
	count,
	environmentCount,
	selected,
	onClick,
}: {
	category: InfraStartFailureCategory;
	count: number;
	environmentCount: number;
	selected: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			data-testid={`start-failures-category-${category}`}
			onClick={onClick}
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "flex-start",
				gap: 4,
				padding: "12px 16px",
				border: "none",
				borderRadius: "var(--radius-4)",
				background: selected ? "var(--surface-2)" : "var(--surface-1)",
				boxShadow: selected ? "var(--shadow-ring-strong)" : "var(--shadow-ring)",
				fontFamily: "inherit",
				textAlign: "left",
				cursor: "pointer",
			}}
		>
			<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
				<Swatch category={category} />
				<span style={captionStyle({ color: "var(--fg-2)" })}>
					{START_FAILURE_CATEGORY_LABELS[category]}
				</span>
			</span>
			<span
				data-testid={`start-failures-count-${category}`}
				style={{
					fontSize: "var(--text-heading)",
					fontWeight: 600,
					color: count > 0 ? START_FAILURE_CATEGORY_COLORS[category] : "var(--fg-3)",
					fontVariantNumeric: "tabular-nums",
				}}
			>
				{count}
			</span>
			<span style={captionStyle()}>{environmentCount} 環境</span>
			<span style={captionStyle({ color: "var(--fg-4)" })}>{CATEGORY_HINTS[category]}</span>
		</button>
	);
}

function Swatch({ category }: { category: InfraStartFailureCategory }) {
	return (
		<span
			style={{
				width: 10,
				height: 10,
				borderRadius: "var(--radius-2)",
				background: START_FAILURE_CATEGORY_COLORS[category],
				flexShrink: 0,
			}}
		/>
	);
}

/** 日ごとの分類別件数の積み上げ縦棒。棒の title に日付と内訳を出す。 */
function DailyChart({ days }: { days: DailyCounts[] }) {
	const max = Math.max(1, ...days.map((d) => d.total));
	const labelEvery = Math.ceil(days.length / 8);
	return (
		<div
			data-testid="start-failures-chart"
			style={{
				padding: 16,
				boxShadow: "var(--shadow-ring)",
				borderRadius: "var(--radius-4)",
			}}
		>
			<div style={{ display: "flex", gap: 8 }}>
				<div
					style={captionStyle({
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
						height: PLOT_HEIGHT,
						textAlign: "right",
						fontVariantNumeric: "tabular-nums",
					})}
				>
					<span>{max}</span>
					<span>0</span>
				</div>
				<div style={{ flex: 1, minWidth: 0 }}>
					<div
						style={{
							display: "flex",
							alignItems: "flex-end",
							gap: 2,
							height: PLOT_HEIGHT,
							borderBottom: "1px solid var(--line-1)",
						}}
					>
						{days.map((day) => (
							<div
								key={day.date}
								title={dayTitle(day)}
								style={{
									flex: 1,
									maxWidth: 24,
									height: "100%",
									display: "flex",
									flexDirection: "column-reverse",
								}}
							>
								{START_FAILURE_CATEGORIES.map(
									(category) =>
										day.counts[category] > 0 && (
											<div
												key={category}
												style={{
													height: `${(day.counts[category] / max) * 100}%`,
													background: START_FAILURE_CATEGORY_COLORS[category],
												}}
											/>
										),
								)}
							</div>
						))}
					</div>
					<div style={{ display: "flex", gap: 2, marginTop: 4 }}>
						{days.map((day, i) => (
							<span
								key={day.date}
								style={captionStyle({
									flex: 1,
									maxWidth: 24,
									whiteSpace: "nowrap",
									overflow: "visible",
								})}
							>
								{i % labelEvery === 0 ? shortDate(day.date) : ""}
							</span>
						))}
					</div>
				</div>
			</div>
			<div style={{ display: "flex", flexWrap: "wrap", columnGap: 14, rowGap: 4, marginTop: 12 }}>
				{START_FAILURE_CATEGORIES.map((category) => (
					<span key={category} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
						<Swatch category={category} />
						<span style={captionStyle({ color: "var(--fg-2)" })}>
							{START_FAILURE_CATEGORY_LABELS[category]}
						</span>
					</span>
				))}
			</div>
		</div>
	);
}

function shortDate(date: string): string {
	const [, month, day] = date.split("-");
	return `${Number(month)}/${Number(day)}`;
}

function dayTitle(day: DailyCounts): string {
	const parts = START_FAILURE_CATEGORIES.filter((c) => day.counts[c] > 0).map(
		(c) => `${START_FAILURE_CATEGORY_LABELS[c]} ${day.counts[c]}`,
	);
	return `${shortDate(day.date)}: ${parts.length === 0 ? "なし" : parts.join("・")}`;
}

const numericCellStyle = captionStyle({
	...cellStyle,
	textAlign: "right",
	fontVariantNumeric: "tabular-nums",
});

function InstanceBreakdownTable({ rows }: { rows: InstanceBreakdownRow[] }) {
	return (
		<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
			<thead>
				<tr style={captionStyle({ textAlign: "left" })}>
					<th style={{ ...cellStyle, fontWeight: 500, width: 96 }}>サイズ</th>
					<th style={{ ...cellStyle, fontWeight: 500 }}>EC2 インスタンス型</th>
					{START_FAILURE_CATEGORIES.map((category) => (
						<th key={category} style={{ ...cellStyle, fontWeight: 500, textAlign: "right" }}>
							{START_FAILURE_CATEGORY_LABELS[category]}
						</th>
					))}
					<th style={{ ...cellStyle, fontWeight: 500, textAlign: "right", width: 64 }}>合計</th>
				</tr>
			</thead>
			<tbody className="select-text">
				{rows.map((row) => (
					<tr key={`${row.instanceSize}:${row.instanceType ?? ""}`}>
						<td style={captionStyle({ ...cellStyle, color: "var(--fg-1)" })}>
							{formatInstanceSize(row.instanceSize)}
						</td>
						<td style={captionStyle({ ...cellStyle, color: "var(--fg-1)" })}>
							{row.instanceType ?? "—"}
						</td>
						{START_FAILURE_CATEGORIES.map((category) => (
							<td
								key={category}
								style={{
									...numericCellStyle,
									color:
										row.counts[category] > 0
											? START_FAILURE_CATEGORY_COLORS[category]
											: "var(--fg-4)",
								}}
							>
								{row.counts[category]}
							</td>
						))}
						<td style={{ ...numericCellStyle, color: "var(--fg-1)", fontWeight: 600 }}>
							{row.total}
						</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

function RecentTable({ entries }: { entries: InfraStartFailure[] }) {
	const [expandedId, setExpandedId] = useState<string | null>(null);
	return (
		<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
			<thead>
				<tr style={captionStyle({ textAlign: "left" })}>
					<th style={{ ...cellStyle, fontWeight: 500, width: 144 }}>日時</th>
					<th style={{ ...cellStyle, fontWeight: 500 }}>環境</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 160 }}>インスタンス</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 152 }}>分類</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 240 }}>エラーコード</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 56 }} />
				</tr>
			</thead>
			<tbody className="select-text">
				{entries.map((entry) => {
					const expanded = expandedId === entry.id;
					return (
						<RecentRow
							key={entry.id}
							entry={entry}
							expanded={expanded}
							onToggle={() => setExpandedId(expanded ? null : entry.id)}
						/>
					);
				})}
			</tbody>
		</table>
	);
}

function RecentRow({
	entry,
	expanded,
	onToggle,
}: {
	entry: InfraStartFailure;
	expanded: boolean;
	onToggle: () => void;
}) {
	return (
		<>
			<tr data-testid={`start-failure-${entry.id}`}>
				<td style={captionStyle(cellStyle)}>{formatDateTime(entry.occurredAt)}</td>
				<td style={cellStyle}>
					<div
						title={entry.environmentId}
						style={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							fontSize: "var(--text-caption)",
							color: "var(--fg-1)",
						}}
					>
						{entry.environmentName}
					</div>
					<div
						title={entry.organizationName}
						data-tooltip-if-truncated
						style={captionStyle({
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						})}
					>
						{entry.organizationName}
					</div>
				</td>
				<td style={captionStyle({ ...cellStyle, color: "var(--fg-1)" })}>
					{formatInstanceSize(entry.instanceSize)}
					{entry.instanceType !== null && <div style={captionStyle()}>{entry.instanceType}</div>}
				</td>
				<td style={cellStyle}>
					<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
						<Swatch category={entry.category} />
						<span style={captionStyle({ color: "var(--fg-1)" })}>
							{START_FAILURE_CATEGORY_LABELS[entry.category]}
						</span>
					</span>
					<div style={captionStyle()}>
						{entry.kind === "waitingForCapacity" ? "確保待ちへ" : `失敗 (${entry.reason})`}
					</div>
				</td>
				<td
					style={captionStyle({
						...cellStyle,
						fontFamily: "var(--font-mono)",
						overflowWrap: "anywhere",
					})}
				>
					{entry.errorCodes.length > 0 ? entry.errorCodes.join(", ") : (entry.errorType ?? "—")}
				</td>
				<td style={{ ...cellStyle, textAlign: "right" }}>
					{entry.detail !== null && (
						<button
							type="button"
							data-testid={`start-failure-toggle-${entry.id}`}
							onClick={onToggle}
							className={ACTION_LINK_CLASS}
							style={{ padding: "2px 6px" }}
						>
							{expanded ? "閉じる" : "詳細"}
						</button>
					)}
				</td>
			</tr>
			{expanded && entry.detail !== null && (
				<tr>
					<td colSpan={6} style={{ padding: "0 0 12px" }}>
						<pre
							data-testid={`start-failure-detail-${entry.id}`}
							style={captionStyle({
								margin: 0,
								padding: 12,
								maxHeight: 320,
								overflow: "auto",
								background: "var(--surface-0)",
								borderRadius: "var(--radius-2)",
								fontFamily: "var(--font-mono)",
								color: "var(--fg-2)",
								whiteSpace: "pre-wrap",
								overflowWrap: "anywhere",
							})}
						>
							{entry.detail}
						</pre>
					</td>
				</tr>
			)}
		</>
	);
}
